import { useMemo, useRef } from 'react';
import type { EventCalendarProperties } from '../EventCalendar';
import { AllDayEvents, Events } from '../events';
import { Header } from '../header';
import { useGetDays, useGetSortedEventsInTimezone, useScrollbarWidth } from '../hooks';
import { Hours } from '../hours';
import { LoaderWithStateAnnouncement } from '../loader-with-state-announcement';
import { NowLine } from '../now-line';
import { RowSpacer } from '../row-spacer';
import type { CalendarEvent, TimeGridCalendarMode } from '../types';
import styles from './time-grid-calendar.module.css';

type P<T extends CalendarEvent> = {
  mode: TimeGridCalendarMode;
} & Omit<EventCalendarProperties<T>, 'mode'>;

const TimeGridCalendar = <T extends CalendarEvent>({
  date,
  events,
  isLoading,
  mode,
  renderEvent,
  renderTimeSlot,
  timeSlotDurationInMinutes,
  timezones,
  weekStartsOn,
}: P<T>) => {
  const primaryTimezone = useMemo(() => timezones[0], [timezones]);
  const orderedTimezones = useMemo(() => timezones.toReversed(), [timezones]);
  const bodyReference = useRef<HTMLDivElement>(null);
  const scrollbarWidth = useScrollbarWidth({ bodyReference });
  const days = useGetDays({ date, mode, timezone: primaryTimezone, weekStartsOn });
  const gmtOffsetReferenceDate = days[0] ?? date;
  const { allDayEvents, allDayRowHeightPx, timeSlotEvents } = useGetSortedEventsInTimezone<T>({
    days,
    events,
    primaryTimezone,
  });

  const eventsProperties = {
    days,
    mode,
    renderEvent,
    timezones,
  };

  return (
    <>
      <Header
        days={days}
        gmtOffsetReferenceDate={gmtOffsetReferenceDate}
        mode={mode}
        orderedTimezones={orderedTimezones}
        primaryTimezone={primaryTimezone}
        scrollbarWidth={scrollbarWidth}
      />
      <AllDayEvents
        {...eventsProperties}
        allDayRowHeightPx={allDayRowHeightPx}
        events={allDayEvents}
        scrollbarWidth={scrollbarWidth}
      />
      <div className={styles['event-calendar-content']}>
        <div aria-busy={isLoading} className={styles['event-calendar-body']} ref={bodyReference}>
          <RowSpacer days={days} mode={mode} orderedTimezones={orderedTimezones} />
          <NowLine
            bodyReference={bodyReference}
            days={days}
            mode={mode}
            primaryTimezone={primaryTimezone}
            timezoneCount={timezones.length}
          />
          <Hours
            days={days}
            mode={mode}
            orderedTimezones={orderedTimezones}
            renderTimeSlot={renderTimeSlot}
            timeSlotDurationInMinutes={timeSlotDurationInMinutes}
            timezone={primaryTimezone}
          />
          <Events {...eventsProperties} events={timeSlotEvents} />
        </div>
        <LoaderWithStateAnnouncement isLoading={isLoading} />
      </div>
    </>
  );
};
export default TimeGridCalendar;
